import { memo, useCallback, useMemo, useState } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FormDialog from '@/components/atoms/FormDialog/FormDialog';
import { MultiSelectField, type MultiSelectOption } from '@/components/atoms/MultiSelect';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useCloseModal } from '@/hooks/useCloseModal';
import { useInvitations } from '@/hooks/useInvitations';
import useMembers from '@/hooks/useMembers';
import { useRoles } from '@/hooks/useRoles';
import { useTeams } from '@/hooks/useTeams';
import { useTeamUsers } from '@/hooks/useTeamUsers';
import { useUserRoles } from '@/hooks/useUserRoles';
import { OptionBase } from '@/shared/types/common';
import { RoleType } from '@/shared/types/member';
import { roleSelectOptions } from '@/util/roleOptions';
import { errorMessage } from '@/util/validation/helpers';

interface Props {
  user: {
    email: string;
    role_ids: string[];
    invitation_code?: string;
    user_id?: string;
    name?: string;
    role?: string;
    team_ids?: string[];
  };
}
const ModalRoleEdit = ({ user }: Props) => {
  const { t } = useTranslation();
  const { addUsers, removeUser } = useTeamUsers();
  const { members, updateMember } = useMembers();

  const { updateInvitation } = useInvitations();
  const { removeRole, assignRoles } = useUserRoles(user?.user_id);
  const { roles, availableRoles, listStatus } = useRoles();

  const team_ids = members.find((m) => m.user_id === user?.user_id)?.team_ids ?? user?.team_ids;
  const { teams } = useTeams();

  const defaultTeams =
    team_ids?.reduce((acc, id) => {
      const team = teams.find((t) => t.team_id === id);
      if (team) {
        acc.push({ value: team.team_id, label: team.name });
      }
      return acc;
    }, []) ?? [];

  const defaultRoles =
    user?.role === RoleType.RBAC
      ? roles
          .filter((r) => user?.role_ids?.includes(r.role_id))
          .map((role) => ({ label: role.name, value: role.role_id }))
      : [];

  const form = useForm<{ roles: OptionBase[]; teams: OptionBase[] }>({
    mode: 'onChange',
    defaultValues: { roles: defaultRoles, teams: defaultTeams },
  });
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = form;

  const closeModal = useCloseModal();
  const [isLoading, setIsLoading] = useState(false);

  const teamOptions = teams
    .filter((t) => t.role_ids.length > 0)
    .map((team) => ({ label: team.name, value: team.team_id, description: team.description }));

  const onSubmit = useCallback(
    async (form: { roles: { value: string }[]; teams: { value: string }[] }) => {
      setIsLoading(true);
      const newRoleids = form.roles.map((r) => r.value) as string[];
      const newTeamIds = form.teams.map((r) => r.value) as string[];
      // If user has invitation, update invitation
      if (user.invitation_code) {
        updateInvitation(
          { email: user.email, role: RoleType.RBAC, role_ids: newRoleids, team_ids: newTeamIds },
          {
            onSuccess: () => {
              closeModal();
            },
          }
        );
      } else {
        const removedRoled = user?.role_ids?.filter((role) => !newRoleids.includes(role)) ?? [];

        if (removedRoled.length > 0) {
          await Promise.all(
            removedRoled.map(async (role_id: string) => {
              return removeRole({ user_id: user?.user_id, role_id });
            })
          );
        }
        const addedRoles = newRoleids.filter((role) => !user?.role_ids.includes(role));

        if (addedRoles.length > 0) {
          await assignRoles({ role_ids: newRoleids, user_id: user.user_id });
        }
        const newTeamIds = form.teams.map((r) => r.value) as string[];

        const removedTeamIds = team_ids?.filter((team) => !newTeamIds.includes(team)) ?? [];

        if (removedTeamIds.length > 0) {
          await Promise.all(
            removedTeamIds.map(async (team_id: string) => {
              return removeUser({ team_id: team_id, user_id: user?.user_id });
            })
          );
        }

        const addedTeamIds = newTeamIds.filter((team) => !team_ids?.includes(team));

        await Promise.all(
          addedTeamIds.map(async (team_id: string) => {
            const newTeam = { user_ids: [user.user_id], team_id };

            return addUsers(newTeam);
          })
        );

        if (user?.role !== RoleType.RBAC) {
          updateMember({ user_id: user?.user_id, role: RoleType.RBAC });
        }
        closeModal();
      }
    },
    [
      addUsers,
      assignRoles,
      closeModal,
      removeRole,
      removeUser,
      team_ids,
      updateInvitation,
      updateMember,
      user.email,
      user.invitation_code,
      user?.role,
      user?.role_ids,
      user.user_id,
    ]
  );
  const rolesField = useWatch({ control, name: 'roles' });
  const teamsField = useWatch({ control, name: 'teams' });

  const isFormEmpty = rolesField.length === 0 && teamsField.length === 0;

  const roleOptions: MultiSelectOption[] = useMemo(
    () =>
      roleSelectOptions(availableRoles, roles, {
        system: t('roles.system_roles'),
        custom: t('roles.custom_roles'),
      }),
    [availableRoles, roles, t]
  );

  return (
    <FormDialog
      title={
        <>
          {t('roles.edit_access_for')}
          <span className="ml-1 text-foreground-muted">{user.name ?? user.email}</span>
        </>
      }
      submitLabel={t('common.save')}
      onSubmit={handleSubmit(onSubmit)}
      submitDisabled={isFormEmpty}
      isLoading={isLoading}
      submitOnEnter
      contentClassName="sm:max-w-2xl"
    >
      <Form {...form}>
        <form autoComplete="off" className="space-y-6">
          <div className="input__container">
            <FormField
              control={control}
              name="roles"
              render={({ field }) => {
                const selectedValues = field.value?.map((role: OptionBase) => role.value) || [];

                return (
                  <FormItem>
                    <FormLabel className="text-base font-medium">
                      {t('common.members_roles')}
                    </FormLabel>
                    <FormControl>
                      <MultiSelectField
                        title={t('common.select')}
                        options={roleOptions}
                        sortOptions={false}
                        value={selectedValues}
                        onChange={(selectedStringValues) => {
                          const selectedRoles = selectedStringValues
                            .map((value) => availableRoles.find((role) => role.value === value))
                            .filter((role): role is OptionBase => !!role);
                          field.onChange(selectedRoles);
                        }}
                        placeholder={t('common.roles_placeholder')}
                        disabled={listStatus === 'pending'}
                        error={!!errors?.roles?.message}
                      />
                    </FormControl>
                    {errors?.roles?.message && (
                      <FormMessage>{errorMessage({ field: errors.roles })}</FormMessage>
                    )}
                  </FormItem>
                );
              }}
            />
          </div>

          <div className="input__container">
            <FormField
              control={control}
              name="teams"
              render={({ field }) => {
                const multiSelectOptions: MultiSelectOption[] = teamOptions.map((team) => ({
                  label: team.label,
                  value: team.value,
                  description: team.description,
                }));

                const selectedValues = field.value?.map((team: OptionBase) => team.value) || [];

                return (
                  <FormItem>
                    <FormLabel className="text-base font-medium">{t('common.teams')}</FormLabel>
                    <FormControl>
                      <MultiSelectField
                        title={t('common.select')}
                        options={multiSelectOptions}
                        value={selectedValues}
                        onChange={(selectedStringValues) => {
                          const selectedTeams = selectedStringValues
                            .map((value) => teamOptions.find((team) => team.value === value))
                            .filter(Boolean) as OptionBase[];
                          field.onChange(selectedTeams);
                        }}
                        placeholder={t('teams.select_team')}
                        error={!!errors?.teams?.message}
                      />
                    </FormControl>
                    {errors?.teams?.message && (
                      <FormMessage>{errorMessage({ field: errors.teams })}</FormMessage>
                    )}
                  </FormItem>
                );
              }}
            />
          </div>
        </form>
      </Form>
    </FormDialog>
  );
};

export default memo(ModalRoleEdit);
